import { useCallback, useEffect, useRef } from 'react';

import { second } from '@utils/milliseconds';

import { config } from 'app-config';

import { presenceStore } from './use-journey-presence-store';

export type ActiveUser = {
  userId: string;
  userName: string;
  userPicture: string;
  lastSeen: number;
};

type PresenceEvent =
  | {
      type: 'presence_state';
      activeUsers: ActiveUser[];
      actionEditors: Record<string, string[]>;
    }
  | {
      type: 'user_joined';
      userId: string;
      userName: string;
      userPicture: string;
    }
  | {
      type: 'user_left';
      userId: string;
    }
  | {
      type: 'action_opened';
      userId: string;
      userName: string;
      userPicture: string;
      actionId: string;
    }
  | {
      type: 'action_closed';
      userId: string;
      actionId: string;
    }
  | {
      type: 'journey_updated';
      operation: string;
      journeyId: string;
      actionId?: string;
    };

export const useJourneyPresence = (journeyId: string) => {
  const socketRef = useRef<WebSocket | null>(null);
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const sendMessage = useCallback((message: object) => {
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify(message));
    }
  }, []);

  const sendActionOpened = useCallback(
    (actionId: string) => {
      sendMessage({ type: 'action_opened', actionId });
    },
    [sendMessage]
  );

  const sendActionClosed = useCallback(
    (actionId: string) => {
      sendMessage({ type: 'action_closed', actionId });
    },
    [sendMessage]
  );

  const sendHeartbeat = useCallback(() => {
    sendMessage({ type: 'heartbeat' });
  }, [sendMessage]);

  const handlePresenceMessage = useCallback((event: MessageEvent<string>) => {
    const data: PresenceEvent = JSON.parse(event.data);

    switch (data.type) {
      case 'presence_state': {
        presenceStore.setActiveUsers(data.activeUsers);

        const actionEditorsMap = new Map<string, Set<string>>();
        Object.entries(data.actionEditors).forEach(([actionId, userIds]) => {
          actionEditorsMap.set(actionId, new Set(userIds));
        });
        presenceStore.setActionEditors(actionEditorsMap);
        break;
      }

      case 'user_joined': {
        const currentUsers = presenceStore.useStore.getState().activeUsers;
        const exists = currentUsers.some((u) => u.userId === data.userId);
        if (!exists) {
          presenceStore.setActiveUsers([
            ...currentUsers,
            {
              userId: data.userId,
              userName: data.userName,
              userPicture: data.userPicture,
              lastSeen: Date.now() / second,
            },
          ]);
        }
        break;
      }

      case 'user_left': {
        const currentUsers = presenceStore.useStore.getState().activeUsers;
        presenceStore.setActiveUsers(currentUsers.filter((u) => u.userId !== data.userId));

        const currentEditors = presenceStore.useStore.getState().actionEditors;
        const next = new Map(currentEditors);
        next.forEach((editors) => {
          editors.delete(data.userId);
          if (editors.size === 0) {
            next.delete(data.userId);
          }
        });
        presenceStore.setActionEditors(next);
        break;
      }

      case 'action_opened': {
        const state = presenceStore.useStore.getState();
        if (state.currentUserId === data.userId) return;
        const currentEditors = state.actionEditors;
        const next = new Map(currentEditors);
        const editors = next.get(data.actionId) || new Set();
        editors.add(data.userId);
        next.set(data.actionId, editors);
        presenceStore.setActionEditors(next);
        break;
      }

      case 'action_closed': {
        const state = presenceStore.useStore.getState();
        if (state.currentUserId === data.userId) return;
        const currentEditors = state.actionEditors;
        const next = new Map(currentEditors);
        const editors = next.get(data.actionId);
        if (editors) {
          editors.delete(data.userId);
          if (editors.size === 0) {
            next.delete(data.actionId);
          } else {
            next.set(data.actionId, editors);
          }
        }
        presenceStore.setActionEditors(next);
        break;
      }
    }
  }, []);

  useEffect(() => {
    if (!journeyId) return;

    const wsUrl = `${config.WS_URL}/journey/${journeyId}/presence/`;
    const socket = new WebSocket(wsUrl);
    socketRef.current = socket;

    socket.onmessage = (event) => {
      handlePresenceMessage(event);
    };

    socket.onerror = (error) => {
      console.error('[Presence] WebSocket error:', error);
    };

    heartbeatIntervalRef.current = setInterval(sendHeartbeat, 30000);

    return () => {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
      if (socketRef.current) {
        socketRef.current.close();
      }
      presenceStore.reset();
    };
  }, [journeyId, handlePresenceMessage, sendHeartbeat]);

  return {
    sendActionOpened,
    sendActionClosed,
  };
};
